import React, { useState, useEffect } from "react";
import SEO from "../seo";
const OrderFulfillment = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "pre-setup-requirements",
        "managing-orders",
        "tips-faster-fulfillment",
        "conclusion",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Order Fulfillment | xPage Help Center"
        description="Learn how to efficiently fulfill orders with xPage. Configure shipping settings, manage orders, optimize fulfillment speed, and ensure smooth delivery for your customers."
        keywords="xPage, order fulfillment, shipping settings, managing orders, fast fulfillment, logistics, dispatch, shipping optimization, order processing, ecommerce"
        canonical="https://help.xpage.ai/help/store-operation/Order%20Fulfillment"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header className="mb-6">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Order Fulfillment in xPage
          </h1>
          <p className="text-sm text-systemLabel">
            Efficient order fulfillment starts with properly configuring your
            shipping and logistics settings. In xPage, you can manage all
            fulfillment-related settings directly from your admin dashboard.
          </p>
        </header>

        {/* Pre-Setup Requirements Section */}
        <section id="pre-setup-requirements" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Pre-Setup Requirements
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Before processing orders, ensure the following are configured:
            </p>

            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Requirement
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Description
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Shipping Rates
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Define rates based on location, weight, or order value.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Shipping Locations
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Specify regions or countries you ship to.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Dispatch Settings
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Set default carriers, processing times, and handling
                    preferences.
                  </td>
                </tr>
              </tbody>
            </table>

            <p className="text-sm text-systemLabel">
              These settings help streamline the fulfillment process and ensure
              accurate shipping costs.
            </p>
          </div>
        </section>

        {/* Managing Orders Section */}
        <section id="managing-orders" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Managing Orders
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Once an order is placed:
            </p>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Go to the Orders page in your xPage admin.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  View order details, including items, customer information, and
                  shipping requirements.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Arrange shipment using the pre-configured shipping and
                  logistics settings.
                </span>
              </li>
            </ol>
          </div>
        </section>

        {/* Tips for Faster Fulfillment Section */}
        <section id="tips-faster-fulfillment" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Tips for Faster Fulfillment
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Tip</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Description
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Use xPage Shipping Tools
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    The shipping page allows you to manage multiple orders at
                    once, print labels, and track shipments.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Optimize Costs
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Set tiered rates or use weight-based pricing to reduce
                    shipping expenses.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Automate When Possible
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Configure default carriers and dispatch rules to save time
                    on repetitive tasks.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Conclusion Section */}
        <section id="conclusion" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Conclusion
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              xPage's built-in shipping and fulfillment system gives you
              flexibility, speed, and control, helping you deliver orders
              efficiently while maintaining a professional customer experience.
            </p>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#pre-setup-requirements"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "pre-setup-requirements"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Pre-Setup Requirements
              </a>
            </li>
            <li>
              <a
                href="#managing-orders"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "managing-orders"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Managing Orders
              </a>
            </li>
            <li>
              <a
                href="#tips-faster-fulfillment"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "tips-faster-fulfillment"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Tips for Faster Fulfillment
              </a>
            </li>
            <li>
              <a
                href="#conclusion"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "conclusion"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Conclusion
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default OrderFulfillment;
